import { API_URL } from '@/lib/api';

// ─── Account API Functions ──────────────────────────────────────────
// Standalone fetch functions extracted from AccountContext.
// These are used by React Query hooks in lib/queries/account.ts.

export interface UserProfile {
  id: string;
  user_id: string;
  name: string | null;
  image: string | null;
  tailTag?: string;
  created_at: string;
  updated_at: string;
}

export interface Collectible {
  name: string;
  description?: string;
  preview_url?: string;
  image_url?: string;
}

export async function fetchAccount(token: string): Promise<UserProfile> {
  const response = await fetch(`${API_URL}/account/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail?.[0]?.msg || `Failed to fetch account (${response.status})`);
  }
  return response.json();
}

export async function updateAccountAPI(
  token: string,
  updates: Partial<Pick<UserProfile, 'name' | 'image'>>,
): Promise<UserProfile> {
  const response = await fetch(`${API_URL}/account/me`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail?.[0]?.msg || 'Failed to update account');
  }
  return response.json();
}

export async function deleteAccountAPI(token: string): Promise<void> {
  const response = await fetch(`${API_URL}/account/me`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail?.[0]?.msg || 'Failed to delete account');
  }
}

export async function fetchCollectibles(token: string): Promise<Collectible[]> {
  const resp = await fetch(`${API_URL}/account/collectibles`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`Failed to fetch collectibles (${resp.status})`);
  }
  const data = await resp.json();
  return Array.isArray(data) ? data : [];
}

/**
 * For each collectible, fetch the SVG/image content and return
 * an array of SVG strings or fallback URLs aligned with the collectibles array.
 */
export async function fetchCollectibleSvgs(
  collectibles: Collectible[],
): Promise<(string | null)[]> {
  const svgs: (string | null)[] = collectibles.map(() => null);

  await Promise.all(
    collectibles.map(async (item, idx) => {
      const preview = item?.preview_url;
      const modelUrl = item?.image_url;

      if (preview) {
        try {
          const r = await fetch(preview);
          if (!r.ok) {
            svgs[idx] = preview;
            return;
          }
          const contentType = (r.headers.get('content-type') || '').toLowerCase();
          if (contentType.includes('svg') || contentType.includes('xml') || preview.toLowerCase().endsWith('.svg')) {
            svgs[idx] = await r.text();
          } else {
            svgs[idx] = preview;
          }
        } catch {
          svgs[idx] = preview;
        }
        return;
      }

      if (!modelUrl) return;

      if (/\.gltf?$|\.glb($|\?|#)/i.test(modelUrl)) {
        svgs[idx] = modelUrl;
        return;
      }

      try {
        const r = await fetch(modelUrl);
        if (!r.ok) {
          svgs[idx] = modelUrl;
          return;
        }
        const contentType = (r.headers.get('content-type') || '').toLowerCase();
        if (contentType.includes('svg') || contentType.includes('xml') || modelUrl.toLowerCase().endsWith('.svg')) {
          svgs[idx] = await r.text();
        } else {
          svgs[idx] = modelUrl;
        }
      } catch {
        svgs[idx] = modelUrl;
      }
    }),
  );

  return svgs;
}